import React from 'react';
import { Box, Typography, Button, FormGroup, FormControlLabel, Checkbox, MenuItem, TextField } from '@mui/material';
import { Dayjs } from 'dayjs';
import DatePickerComponent from '../../../../components/DatePickerComponent';
import { SportButtonEventStatus } from '../../../../services/Sports/ButtonEvents/types';
import { COLORS, STATUS_CONFIG } from './Constants';

const { tableBackgroundColor, tableHeaderColor, borderColorDarkTheme, fontColorDarkTheme } = COLORS;

const datePickerStyles = {
  '& .MuiInputBase-root': {
    backgroundColor: tableBackgroundColor,
    color: fontColorDarkTheme,
    height: 36,
    fontSize: 13,
  },
  '& .MuiInputBase-input': { padding: '8px 10px' },
  '& .MuiInputLabel-root': { color: 'gray', fontSize: 12, transform: 'translate(12px, 9px) scale(1)' },
  '& .MuiInputLabel-shrink': { transform: 'translate(12px, -6px) scale(0.75)' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: borderColorDarkTheme },
};

interface FiltersSidebarProps {
  devices: { id: number; name: string }[];
  deviceId?: number;
  status?: SportButtonEventStatus;
  search: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  isLoading: boolean;
  onDeviceChange: (id: number | undefined) => void;
  onStatusToggle: (status: SportButtonEventStatus) => void;
  onSearchChange: (value: string) => void;
  onStartDateChange: (date: Dayjs | null) => void;
  onEndDateChange: (date: Dayjs | null) => void;
  onApply: () => void;
  onClear: () => void;
}

const FiltersSidebar = ({
  devices,
  deviceId,
  status,
  search,
  startDate,
  endDate,
  isLoading,
  onDeviceChange,
  onStatusToggle,
  onSearchChange,
  onStartDateChange,
  onEndDateChange,
  onApply,
  onClear,
}: FiltersSidebarProps) => {
  return (
    <Box sx={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', p: 3, backgroundColor: tableHeaderColor }}>
      <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'gray' }}>
        Período específico
      </Typography>
      <Box sx={{ mt: 1, mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={datePickerStyles}>
          <DatePickerComponent label="Data Inicial" value={startDate} disableFuture onChange={onStartDateChange} />
        </Box>
        <Box sx={datePickerStyles}>
          <DatePickerComponent label="Data Final" value={endDate} disableFuture onChange={onEndDateChange} />
        </Box>
      </Box>

      <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'gray' }}>
        Dispositivo
      </Typography>
      <Box sx={{ mt: 1, mb: 3 }}>
        <TextField
          select
          fullWidth
          size="small"
          value={deviceId ?? ''}
          onChange={(e) => onDeviceChange(e.target.value === '' ? undefined : Number(e.target.value))}
          sx={{ ...datePickerStyles, '& .MuiSelect-select': { color: fontColorDarkTheme } }}
        >
          <MenuItem value="">Todos</MenuItem>
          {devices.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'gray' }}>
        Buscar
      </Typography>
      <Box sx={{ mt: 1, mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Nome do device ou ID controle"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={datePickerStyles}
        />
      </Box>

      <Typography variant="overline" sx={{ fontWeight: 'bold', color: 'gray' }}>
        Status
      </Typography>
      <FormGroup sx={{ mb: 4, mt: 1 }}>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                checked={status === key}
                onChange={() => onStatusToggle(key as SportButtonEventStatus)}
                sx={{ color: 'gray', '&.Mui-checked': { color: '#58a6ff' } }}
              />
            }
            label={cfg.label}
          />
        ))}
      </FormGroup>

      <Button fullWidth variant="contained" color="primary" onClick={() => onApply()} disabled={isLoading}>
        Aplicar Filtros
      </Button>
      <Button fullWidth variant="outlined" onClick={() => onClear()} sx={{ mt: 1, color: 'gray', borderColor: borderColorDarkTheme }}>
        Limpar
      </Button>
    </Box>
  );
};

export default FiltersSidebar;
